/**
 * The fields this firm added to leads and clients for itself.
 *
 * Every firm keeps something the product did not anticipate — a referring chamber, a
 * visa category, a matter number from the system they left. Without somewhere to put
 * it they write it in a note, where nothing can filter, sort or report on it.
 *
 * ---------------------------------------------------------------------------
 * WHY THE CLIENT-VISIBLE CONTROL LOOKS LIKE THAT
 * ---------------------------------------------------------------------------
 *
 * A custom field is a hole through the three mechanisms that keep internal reasoning
 * away from clients: the event registry, the portal serializers and the field guard.
 * None of them knew this field would exist.
 *
 * So it is off by default, it is the last thing on the form rather than sitting beside
 * the other checkboxes, and turning it on says in words what it does. The failure this
 * is written against is an administrator adding "Risk note — possible sham marriage"
 * and the client reading it in their own portal. Nobody would have decided that; it
 * would just happen.
 *
 * The change is audited on its own, separately from every other edit, because "who
 * made this visible to clients, and when" is the question asked afterwards.
 */

import {
  ConflictError,
  STAFF,
  qk,
  useApi,
  useApiMutation,
  usePaginatedList,
  type CustomFieldDefinition,
  type CustomFieldEntity,
  type CustomFieldType,
} from '@legalcrm/api';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Checkbox,
  ConfirmModal,
  Input,
  Modal,
  Select,
  Table,
  Textarea,
  useToast,
  type Column,
} from '@legalcrm/ui';
import { useState } from 'react';

import { Can } from '@/auth/permissions';

const ENTITIES: Array<{ value: CustomFieldEntity; label: string; blurb: string }> = [
  {
    value: 'lead',
    label: 'Leads',
    blurb: 'Shown on every enquiry, and available as a column when importing leads.',
  },
  {
    value: 'client',
    label: 'Clients',
    blurb: 'Shown on every client record, and available when importing clients.',
  },
];

const FIELD_TYPES: Array<{ value: CustomFieldType; label: string; hint: string }> = [
  { value: 'text', label: 'Text', hint: 'A short line of text.' },
  { value: 'long_text', label: 'Long text', hint: 'A paragraph.' },
  { value: 'number', label: 'Number', hint: 'Stored exactly — no rounding.' },
  { value: 'date', label: 'Date', hint: 'Written as YYYY-MM-DD.' },
  { value: 'boolean', label: 'Yes / no', hint: 'A tick box.' },
  { value: 'select', label: 'Choose one', hint: 'A list you define below.' },
  { value: 'multi_select', label: 'Choose several', hint: 'A list you define below.' },
  { value: 'email', label: 'Email address', hint: 'Checked for an @.' },
  { value: 'phone', label: 'Telephone', hint: 'Free text — numbers vary too much.' },
  { value: 'url', label: 'Web address', hint: 'Must start http:// or https://.' },
];

const TYPE_LABELS = Object.fromEntries(FIELD_TYPES.map((t) => [t.value, t.label]));

/** The two types that carry a list of options. Everything about the form keys off this. */
function needsOptions(type: CustomFieldType): boolean {
  return type === 'select' || type === 'multi_select';
}

export function CustomFieldsPage() {
  const [entity, setEntity] = useState<CustomFieldEntity>('lead');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<CustomFieldDefinition | null>(null);
  const [removing, setRemoving] = useState<CustomFieldDefinition | null>(null);

  const list = usePaginatedList<CustomFieldDefinition>(
    qk.list('custom-fields', { entity }),
    STAFF.customFields,
    { entity, limit: 100 },
  );

  const columns: Column<CustomFieldDefinition>[] = [
    {
      key: 'label',
      header: 'Field',
      render: (row) => (
        <div>
          <span className="text-ink">{row.label}</span>
          {row.help_text && (
            <p className="text-xs text-ink-muted">{row.help_text}</p>
          )}
        </div>
      ),
    },
    {
      key: 'field_type',
      header: 'Type',
      hideOnMobile: true,
      render: (row) => (
        <span className="text-ink-muted">
          {TYPE_LABELS[row.field_type] ?? row.field_type}
          {needsOptions(row.field_type) && row.options.length > 0 && (
            <span className="block text-xs text-ink-subtle">
              {row.options.join(', ')}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'key',
      header: 'Column name',
      hideOnMobile: true,
      render: (row) => (
        // Shown because it is what goes in the header row of an import file, and
        // somebody preparing a spreadsheet needs to know it exactly.
        <code className="rounded bg-surface-sunken px-1.5 py-0.5 text-xs text-ink-muted">
          {row.key}
        </code>
      ),
    },
    {
      key: 'rules',
      header: 'Rules',
      render: (row) => (
        <div className="flex flex-wrap gap-1">
          {row.is_required && <Badge tone="warning">Required</Badge>}
          {/* Never colour alone — the label says it, per CR-25. */}
          {row.is_client_visible ? (
            <Badge tone="warning">Clients can see this</Badge>
          ) : (
            <Badge tone="internal">Internal only</Badge>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (row) => (
        <Can module="organization" action="configure">
          <div className="flex justify-end gap-1">
            <Button variant="ghost" size="sm" onClick={() => setEditing(row)}>
              Edit
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setRemoving(row)}>
              Remove
            </Button>
          </div>
        </Can>
      ),
    },
  ];

  const current = ENTITIES.find((option) => option.value === entity)!;

  return (
    <>
      <Card className="mb-4">
        <CardBody>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <Select
              label="Fields on"
              containerClassName="w-56"
              value={entity}
              onChange={(event) =>
                setEntity(event.currentTarget.value as CustomFieldEntity)
              }
              options={ENTITIES.map((option) => ({
                value: option.value,
                label: option.label,
              }))}
              hint={current.blurb}
            />
            <Can module="organization" action="configure">
              <Button variant="primary" onClick={() => setCreating(true)}>
                New field
              </Button>
            </Can>
          </div>
        </CardBody>
      </Card>

      <Table
        caption={`Custom fields on ${current.label.toLowerCase()}`}
        columns={columns}
        rows={list.items}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        error={list.error}
        onRetry={() => void list.refetch()}
        emptyTitle="No fields of your own yet"
        emptyDescription={`Anything this firm records about ${current.label.toLowerCase()} that the standard form has no room for.`}
      />

      <FieldDialog
        open={creating}
        onClose={() => setCreating(false)}
        entity={entity}
        field={null}
      />
      <FieldDialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        entity={entity}
        field={editing}
      />
      <RemoveFieldDialog field={removing} onClose={() => setRemoving(null)} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Adding and editing
// ---------------------------------------------------------------------------

/**
 * Not a `useZodForm`, unlike every other form in this app.
 *
 * The shape genuinely changes as you fill it in — the options list exists only for two
 * of the ten types — and a zod schema that is valid for one type and not another has to
 * be rebuilt on every keystroke. Plain state is less machinery here, not more.
 *
 * `key` and `field_type` are absent when editing. The server refuses both: renaming a
 * key would orphan every value stored under the old one, and changing a type would
 * leave "n/a" sitting in a column now declared a date.
 */
function FieldDialog({
  open,
  onClose,
  entity,
  field,
}: {
  open: boolean;
  onClose: () => void;
  entity: CustomFieldEntity;
  field: CustomFieldDefinition | null;
}) {
  const api = useApi();
  const { toast } = useToast();
  const editingExisting = field !== null;

  const [label, setLabel] = useState('');
  const [helpText, setHelpText] = useState('');
  const [fieldType, setFieldType] = useState<CustomFieldType>('text');
  const [optionsText, setOptionsText] = useState('');
  const [isRequired, setIsRequired] = useState(false);
  const [isClientVisible, setIsClientVisible] = useState(false);
  // Which record the form was last filled from, so reopening on a different row
  // refills it. `useEffect` on `field` would fight the user's typing.
  const [filledFrom, setFilledFrom] = useState<string | null>(null);

  const identity = field?.id ?? (open ? 'new' : null);
  if (open && filledFrom !== identity) {
    setFilledFrom(identity);
    setLabel(field?.label ?? '');
    setHelpText(field?.help_text ?? '');
    setFieldType(field?.field_type ?? 'text');
    setOptionsText((field?.options ?? []).join('\n'));
    setIsRequired(field?.is_required ?? false);
    setIsClientVisible(field?.is_client_visible ?? false);
  }

  const options = optionsText
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  const save = useApiMutation<CustomFieldDefinition, void>(
    {
      mutationFn: () => {
        const body = {
          label,
          help_text: helpText,
          is_required: isRequired,
          is_client_visible: isClientVisible,
          options: needsOptions(fieldType) ? options : [],
        };
        return field
          ? api.patch<CustomFieldDefinition>(`${STAFF.customField(field.id)}/`, body)
          : api.post<CustomFieldDefinition>(`${STAFF.customFields}/`, {
              ...body,
              entity,
              field_type: fieldType,
            });
      },
      invalidates: [qk.all('custom-fields')],
    },
    {
      onSuccess: (saved) => {
        toast({
          title: editingExisting ? 'Field updated' : 'Field added',
          description: saved.is_client_visible
            ? `${saved.label} — clients can see this field.`
            : saved.label,
          tone: 'success',
        });
        onClose();
      },
      onError: (error) => {
        toast({
          title: 'The field was refused',
          description:
            error instanceof ConflictError
              ? error.detail
              : 'The field could not be saved. Try again.',
          tone: 'warning',
        });
      },
    },
  );

  const typeHint = FIELD_TYPES.find((option) => option.value === fieldType)?.hint;
  const blocked =
    !label.trim() || (needsOptions(fieldType) && options.length === 0);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editingExisting ? `Edit ${field.label}` : 'New field'}
      description={
        editingExisting
          ? 'The column name and the type are fixed once a field exists — changing either would orphan what people have already typed.'
          : `A field of this firm's own, on every ${entity}.`
      }
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (!blocked) void save.mutateAsync();
        }}
      >
        <Input
          label="Label"
          value={label}
          onChange={(event) => setLabel(event.currentTarget.value)}
          required
          autoFocus
          autoComplete="off"
          hint={
            editingExisting
              ? `Stored under ${field.key}, which does not change.`
              : 'What everybody will read. The column name is worked out from this.'
          }
        />

        <Input
          label="Help text"
          value={helpText}
          onChange={(event) => setHelpText(event.currentTarget.value)}
          hint="Optional. Shown under the field to whoever is filling it in."
        />

        <Select
          label="Type"
          value={fieldType}
          disabled={editingExisting}
          onChange={(event) =>
            setFieldType(event.currentTarget.value as CustomFieldType)
          }
          options={FIELD_TYPES.map((option) => ({
            value: option.value,
            label: option.label,
          }))}
          hint={
            editingExisting
              ? 'Fixed. A field that changes type leaves the old values behind it.'
              : typeHint
          }
        />

        {needsOptions(fieldType) && (
          <Textarea
            label="Options"
            rows={5}
            value={optionsText}
            onChange={(event) => setOptionsText(event.currentTarget.value)}
            required
            hint="One per line. Anything not on this list is refused, including from an import file."
          />
        )}

        <Checkbox
          label="Required"
          checked={isRequired}
          onChange={(event) => setIsRequired(event.currentTarget.checked)}
          hint={`A new ${entity} cannot be saved without it, and an import file must have this column.`}
        />

        {/*
          Deliberately separated from the checkbox above and given its own frame. This
          is the only control on the screen that can put something in front of a client,
          and it should not read as one more option in a row.
        */}
        <div className="rounded-md border border-line bg-surface-sunken p-3">
          <Checkbox
            label="Clients can see this field"
            checked={isClientVisible}
            onChange={(event) => setIsClientVisible(event.currentTarget.checked)}
            hint="Off by default. Turned on, whatever anybody types here appears in the client's own portal — so it is the wrong place for an internal assessment, a risk note or a view about the client."
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" loading={save.isPending} disabled={blocked}>
            {editingExisting ? 'Save field' : 'Add field'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Removing a field.
 *
 * Soft on the server: the values stay in place, so a field removed by mistake comes
 * back with its data. The dialog says so, because "Remove" that turns out to be
 * reversible is worth knowing before you decide, and the alternative is somebody not
 * daring to tidy up.
 */
function RemoveFieldDialog({
  field,
  onClose,
}: {
  field: CustomFieldDefinition | null;
  onClose: () => void;
}) {
  const api = useApi();
  const { toast } = useToast();

  const remove = useApiMutation<void, void>(
    {
      mutationFn: () => api.delete<void>(`${STAFF.customField(field!.id)}/`),
      invalidates: [qk.all('custom-fields')],
    },
    {
      onSuccess: () => {
        toast({
          title: 'Field removed',
          description: `${field?.label} is no longer on the form. What people typed into it is kept.`,
          tone: 'success',
        });
        onClose();
      },
    },
  );

  return (
    <ConfirmModal
      open={field !== null}
      onClose={onClose}
      title={`Remove ${field?.label ?? 'this field'}?`}
      confirmLabel="Remove field"
      destructive
      loading={remove.isPending}
      onConfirm={() => void remove.mutateAsync()}
      consequences={
        <>
          <p>
            It comes off every {field?.entity} form and out of the import columns, and
            stops being shown to anyone — clients included.
          </p>
          <p className="mt-2">
            What people already typed into it is kept. Adding the field back under the
            same column name brings those values with it.
          </p>
        </>
      }
    />
  );
}
